<template>
	<div class="subfields">
		<BaseCmsLabel code="wc_value_card_subtitle" tag="div" class="subfields-title" />
		<div :class="['input-field input-field-gift', {'floated-label': floatingLabel, 'field-error': fieldError, 'field-success': !fieldError && gift }]">
			<input type="text" :name="field.name" :id="field.name" placeholder=" " maxlength="19" :readonly="gift" v-model="value" @focus="onFocus" @blur="onBlur" @input="onKeyUp($event)" @keyup="onKeyUp($event)" />
			<label :for="field.name"><BaseCmsLabel code="wc_value_card_input" /></label>
			<button v-if="!gift" class="btn-white" @click.prevent="getGift('add')" :disabled="disabledButton"><BaseCmsLabel code="wc_value_card_add" /></button>
			<button v-if="gift" class="btn-white" @click.prevent="getGift('remove')"><BaseCmsLabel code="wc_value_card_remove" /></button>
			<span class="error" v-if="fieldError" v-html="errorMessage" />
			<span class="error gift-cnt" v-if="giftMessage">{{ giftMessage }}</span>
			<span class="error gift-cnt success" v-if="gift">{{ giftMessageSuccess }}</span>
		</div>
	</div>
</template>

<script setup>
	import { useField, defineRule, useValidateField } from 'vee-validate'

	const labels = useLabels();
	const webshop = useWebshop();
	const endpoints = useEndpoints();
	const props = defineProps({
		field: {
			type: null,
			default: false,
		},
		data: {
			type: null,
			default: false,
		}
	})
	const gift = ref(null);
	const giftMessage = ref();
	const giftMessageSuccess = ref(null);
	const isDirty = ref(0);
	const errorRequired = labels.get('error_value_card_required');
	const { checkoutLoading } = useCheckout();

	const floatingLabel = ref(false);

	// set floating label on focus
	function onFocus() {
		floatingLabel.value = true;
	}
	function onBlur() {
		if (!value.value) {
			floatingLabel.value = false;
		}
	}

	//disabled button
	const disabledButton = computed(() => {
		const regex = /[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}/i;
		if (!value.value || !regex.test(value.value)) {
			return true;
		}
		return false;
	})

	async function onKeyUp(event) {
		if(event.key != 'Tab' && event.key != 'Escape') {
			isDirty.value = 1;
		}

		//input format
		let valueCardValue =  value.value;
		let valueCardSplit = valueCardValue.split("-").join("");
		valueCardSplit = valueCardSplit.split('').map(function(cur, index){
			if(event.keyCode == 8 || event.keyCode == 46) {
				if(index == 4 || index == 8 || index == 12) {
					return  "-" + cur;
				} else {
					return cur;
				}
			} else if(index == 3 || index == 7 || index == 11) {
				return cur + "-";
			} else {
				return cur;
			}
		}).join('');

		value.value = valueCardSplit;
	}

	// input field
	const fieldIndex = props.data?.findIndex((obj => obj.name == props.field.name));
	const validationShema = (props.field.related_fields) ? 'gift:'+props.field.related_fields : 'gift:null';

	const { setErrors, errorMessage, value, resetField } = useField(props.field.name, validationShema, {
		initialValue: (props.field.value) && props.data[fieldIndex].value
	})

	// clear field on parent checkbox update
	if(props.field.related_fields) {
		const relatedField = props.data?.find(obj => obj.name == props.field.related_fields);
		watch(
			() => relatedField.value,
			async () => {
				if(relatedField.value == 0 && value.value) {
					await getGift('remove');
					isDirty.value = 0;
				}
			}
		)
	}

	// validation rules
	defineRule('gift', (value, [related], ctx) => {
		if(related != 'null') {
			if(ctx.form[related]) {
				if (!value || !value.length || value.match(/^ *$/) !== null) {
					return labels.get('error_value_not_empty');
				}

				const regex = /[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}/i;
				if (!regex.test(value)) {
					return labels.get('error_value_card_alphanumeric');
				}

				if(!props.field.value) {
					return errorRequired;
				}

				if(props.field.value && props.field.value != value) {
					return errorRequired;
				}

				return true;
			}

			return true;
		}

		return true;
	})

	const giftCardAmount = computed(() => {
		const cart = webshop.getCartData();
		const selectedPayments = cart?.cart?.payments?.selected;
		const giftCard = (selectedPayments?.length) ? selectedPayments.find(el => el.widget == 'giftcard') : null;
		const giftCardData = (giftCard?.giftcard_data?.length) ? giftCard.giftcard_data.find(el => el.extra_info != null) : null;
		return giftCardData?.extra_info?.amount || 0;
	});

	// set initial data
	onMounted(() => {
		if(value.value) {
			floatingLabel.value = true;
			gift.value = value.value;
			if(!giftCardAmount.value) setErrors(labels.get('error_value_card_amount'));
		}
	})

	const fieldError = computed(() => {
		if(errorMessage.value && errorMessage.value != errorRequired && isDirty.value || gift.value && !giftCardAmount.value) {
			return true;
		}
		return false;
	})

	// update store field value
	function setStoreValue(value) {
		props.data[fieldIndex].value = value;
	}

	// get API data
	const validate = useValidateField(props.field.name);
	async function getGift(action) {
		gift.value = '';
		checkoutLoading.value = true;
		let removeCode = false;

		if(action == 'remove') {
			value.value = '';
			giftMessageSuccess.value = null;
			removeCode = true;
			setErrors('');
			setStoreValue('');
		}

		await useApi(`${endpoints.get('_post_hapi_customer')}`, {
			method: 'POST',
			body: {"gift_card_code": value.value},
		}).then(async (res) => {
			// on error
			if((res.success == false) || (res.success == true && res.data.extra_info.available_amount == 0)) {
				if(res.success && res.data.extra_info.available_amount == 0) {
					setErrors(labels.get('error_value_card_amount'));
				} else {
					setErrors(labels.get(res.body.label_name));
				}
				giftMessageSuccess.value = null;
				gift.value = null;
				checkoutLoading.value = false;

				if(res.success && res.data.extra_info.available_amount == 0) {
					return labels.get('error_value_card_amount');
				} else {
					return res.body.label_name;
				}
			}

			// on success
			setErrors('')
			if(res.success && res.data.extra_info.available_amount > 0) {
				gift.value = value.value;
				setStoreValue(value.value);
			} else {
				gift.value = null;
			}

			await validate();

			if((res.success && res.data.extra_info.available_amount > 0) || (res.success && removeCode == true)) {
				await useApi(`${endpoints.get('_get_hapi_webshop_cart')}`).then(async (res) => {
					await webshop.fetchCart();
				})
				giftMessageSuccess.value = labels.get(res.data.label_name);
				giftMessage.value = null;
				checkoutLoading.value = false;
			}
		})
	}
</script>

<style scoped lang="less">
	.subfields{
		display: none; margin-top: -1px; padding: 20px 30px; border: 1px solid @blue; position: relative; z-index: 1;
		&.active{display: block;}
		@media (max-width: @ms){padding: 15px 15px 5px;}
	}
	.subfields-title{font-weight: bold; padding-bottom: 7px; font-size: 15px;}

	.input-field-gift{
		display: flex; flex-wrap: wrap; width: 49%; margin-bottom: 0!important;
		@media (max-width: @t){width: 100%!important;}
		@media (max-width: @m){margin-bottom: 20px!important;}
	}
	button{
		color: @blue; font-size: 15px; font-weight: bold; border-color: @blue; position: relative; z-index: 1; min-width: 110px;
		@media (max-width: @t){height: 54px;}
		@media (max-width: @ms){min-width: 95px;}
		&:hover{border-color: @blue; color: @blue;}
		&:disabled{border-color: @borderColor; color: @borderColor;}
	}
	input{width: auto; flex-grow: 1; border-right: 0;}
	.gift-cnt{
		width: 100%; font-size: 13px;
		&.success{color: @blue;}
	}
</style>
